import type { FormulaConfigResponse } from '@edgebox/sync-core-rest-client/dist/services/api';

// TODO: Most of the functionality in here will be obsolete once we're using ProseMirror
//   with a proper syntax tree for the formula editor. At that point we can directly
//   associate nodes in the syntax tree with the relevant function/variable definitions,
//   instead of doing all this string parsing and heuristics based on cursor position.

function isIdentChar(ch: string): boolean {
  return /[A-Za-z0-9_]/.test(ch);
}

function isPathChar(ch: string): boolean {
  return ch === '.' || isIdentChar(ch);
}

function readIdentifierLeft(source: string, idxExclusive: number): { ident: string; start: number; end: number } | null {
  let end = idxExclusive;
  while (end > 0 && source[end - 1] === ' ') end--;
  let start = end;
  while (start > 0 && isIdentChar(source[start - 1])) start--;
  if (start === end) return null;
  return { ident: source.slice(start, end), start, end };
}

function readIdentifierAt(source: string, idx: number): { ident: string; start: number; end: number } | null {
  if (idx < 0) idx = 0;
  if (idx > source.length) idx = source.length;

  let left = idx;
  while (left > 0 && isIdentChar(source[left - 1])) left--;
  let right = idx;
  while (right < source.length && isIdentChar(source[right])) right++;
  if (left === right) return null;
  return { ident: source.slice(left, right), start: left, end: right };
}

function readDottedPathAt(source: string, cursor: number): { path: string; start: number; end: number; segmentIndex: number } | null {
  if (!source) return null;
  if (cursor < 0) cursor = 0;
  if (cursor > source.length) cursor = source.length;

  // If cursor sits on a dot, prefer the identifier to the right.
  if (source[cursor] === '.' && cursor + 1 <= source.length) cursor++;

  let left = cursor;
  while (left > 0 && isPathChar(source[left - 1])) left--;

  let right = cursor;
  while (right < source.length && isPathChar(source[right])) right++;

  if (left === right) return null;

  const raw = source.slice(left, right);
  const trimmed = raw.replace(/^\.+|\.+$/g, '');
  if (!trimmed) return null;

  const start = left + (raw.length - raw.replace(/^\.+/, '').length);
  const end = start + trimmed.length;

  const localCursor = Math.max(0, Math.min(trimmed.length, cursor - start));
  const segments = trimmed.split('.').filter(Boolean);
  if (!segments.length) return null;

  // Determine which segment the cursor is currently on.
  let segmentIndex = 0;
  let offset = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const segStart = offset;
    const segEnd = offset + seg.length;
    const dotPos = segEnd;

    // Cursor within the segment.
    if (localCursor >= segStart && localCursor <= segEnd) {
      segmentIndex = i;
      break;
    }

    // Cursor exactly on the dot after the segment -> prefer next segment.
    if (localCursor === dotPos && i + 1 < segments.length) {
      segmentIndex = i + 1;
      break;
    }

    offset = segEnd + 1; // + '.'
  }

  return { path: trimmed, start, end, segmentIndex };
}

function skipSpacesRight(source: string, idx: number): number {
  let i = idx;
  while (i < source.length && source[i] === ' ') i++;
  return i;
}

function skipWhitespaceRight(source: string, idx: number): number {
  let i = idx;
  while (i < source.length) {
    const ch = source[i];
    if (ch !== ' ' && ch !== '\n' && ch !== '\r' && ch !== '\t') break;
    i++;
  }
  return i;
}

function getFunctionNameAtCursorDirect(source: string, cursor: number): string | null {
  const at = readIdentifierAt(source, cursor);
  if (!at) return null;
  const after = skipSpacesRight(source, at.end);
  if (source[after] !== '(') return null;
  return at.ident;
}

function getEnclosingFunctionNameAtCursor(source: string, cursor: number): string | null {
  let depth = 0;
  for (let i = Math.min(cursor - 1, source.length - 1); i >= 0; i--) {
    const ch = source[i];
    if (ch === ')') {
      depth++;
      continue;
    }
    if (ch === '(') {
      if (depth === 0) {
        const leftIdent = readIdentifierLeft(source, i);
        return leftIdent?.ident ?? null;
      }
      depth--;
    }
  }
  return null;
}

function getTopMostFunctionName(source: string): string | null {
  const i = skipWhitespaceRight(source, 0);
  const at = readIdentifierAt(source, i);
  if (!at) return null;
  const after = skipWhitespaceRight(source, at.end);
  if (source[after] !== '(') return null;
  return at.ident;
}

function describeType(t: unknown): string | undefined {
  if (!t || typeof t !== 'object') return undefined;
  const kind = (t as any).kind;
  if (typeof kind !== 'string') return undefined;

  switch (kind) {
    case 'any':
      return 'any value';
    case 'boolean':
      return 'true/false';
    case 'number':
      return 'a number';
    case 'text':
      return 'text';
    case 'null':
      return 'null';
    case 'list': {
      const inner = describeType((t as any).of);
      return inner ? `a list of ${inner}` : 'a list';
    }
    case 'object': {
      const clarifier = (t as any).clarifier;
      return typeof clarifier === 'string' && clarifier.trim() ? `a ${clarifier}` : 'an object';
    }
    default:
      return undefined;
  }
}

function resolveOverrideTypeAtPath(overrideVar: unknown, segments: string[], segmentIndex: number): { type: any; description?: string } {
  if (!overrideVar || typeof overrideVar !== 'object') return { type: undefined };
  let current: any = overrideVar as any;

  for (let i = 1; i <= segmentIndex; i++) {
    const prop = segments[i];
    if (!prop) return { type: current };
    const props = current && typeof current === 'object' ? ((current as any).properties as Record<string, unknown> | undefined) : undefined;
    const next = props ? (props as any)[prop] : undefined;
    current = next && typeof next === 'object' ? next : undefined;
    if (!current) break;
  }

  const description =
    current && typeof current.description === 'string' && current.description.trim() ? String(current.description).trim() : undefined;
  return { type: current, description };
}

function resolveVariableOrPropertyHelp(
  config: FormulaConfigResponse,
  formula: string,
  cursor: number,
  variableTypesOverride?: Record<string, unknown>
): { name?: string; description?: string } {
  if (!config.variables?.length) return {};

  const pathAt = readDottedPathAt(formula, cursor);
  if (!pathAt) return {};

  const segments = pathAt.path.split('.').filter(Boolean);
  if (!segments.length) return {};

  const baseName = segments[0];
  const variable = config.variables.find((v) => String(v.name) === baseName);
  if (!variable) return {};

  const overrideVarRaw = variableTypesOverride && baseName in variableTypesOverride ? (variableTypesOverride as any)[baseName] : undefined;
  const overrideVar = overrideVarRaw && typeof overrideVarRaw === 'object' ? (overrideVarRaw as any) : undefined;
  const overrideVarDesc =
    overrideVar && typeof overrideVar.description === 'string' && overrideVar.description.trim()
      ? overrideVar.description.trim()
      : undefined;

  // Cursor on the variable name itself.
  if (pathAt.segmentIndex <= 0) {
    const desc = overrideVarDesc ?? (variable.description ? String(variable.description) : undefined);
    return { name: String(variable.name), description: desc };
  }

  // Cursor on a property segment.
  let t: any = variable.type;
  let resolvedToProperty = false;

  // Resolve the override node for this property path (independent from Sync Core config types).
  const overrideAtPath = resolveOverrideTypeAtPath(overrideVar, segments, pathAt.segmentIndex);
  const overridePropDesc = overrideAtPath.description;

  for (let i = 1; i <= pathAt.segmentIndex; i++) {
    const prop = segments[i];
    if (!prop) break;
    if (!t || typeof t !== 'object' || (t as any).kind !== 'object') {
      t = undefined;
      break;
    }
    const props = (t as any).properties as Record<string, unknown> | undefined;
    const next = props ? props[prop] : undefined;
    if (!next) {
      t = undefined;
      break;
    }
    t = next;
    resolvedToProperty = true;
  }

  const shownPath = segments.slice(0, pathAt.segmentIndex + 1).join('.');
  const typeDesc = describeType(t);
  const propName = segments[pathAt.segmentIndex];
  const desc =
    overridePropDesc ??
    (typeDesc ? `Reads "${propName}" from "${segments[0]}". This gives ${typeDesc}.` : `Reads "${propName}" from "${segments[0]}".`);

  // If the override provides a description, always use it (even if Sync Core typed the variable as `any`).
  if (overridePropDesc) return { name: shownPath, description: desc };

  // Even if we can't resolve the exact property type, still show a helpful description.
  return { name: shownPath, description: resolvedToProperty ? desc : `Reads "${propName}" from "${segments[0]}".` };
}

export function resolveFunctionHelp(
  config: FormulaConfigResponse | undefined,
  formula: string,
  cursor: number,
  variableTypesOverride?: Record<string, unknown>
): { name?: string; description?: string } {
  if (!config) return {};

  // 1) If the cursor is directly on a function name, prefer that.
  if (config.functions?.length) {
    const direct = getFunctionNameAtCursorDirect(formula, cursor);
    if (direct) {
      const key = direct.toUpperCase();
      const fn =
        config.functions.find((f) => String(f.name).toUpperCase() === key) ??
        config.functions.find((f) => (f.aliases ?? []).some((a) => String(a).toUpperCase() === key));
      if (fn) return { name: String(fn.name), description: fn.description ? String(fn.description) : undefined };
      return { name: direct };
    }
  }

  // 2) If the cursor touches a variable/property identifier, show that (even inside a function call).
  const varOrProp = resolveVariableOrPropertyHelp(config, formula, cursor, variableTypesOverride);
  if (varOrProp.name) return varOrProp;

  // 3) Otherwise, fall back to the enclosing function call.
  if (config.functions?.length) {
    const enclosing = getEnclosingFunctionNameAtCursor(formula, cursor);
    if (enclosing) {
      const key = enclosing.toUpperCase();
      const fn =
        config.functions.find((f) => String(f.name).toUpperCase() === key) ??
        config.functions.find((f) => (f.aliases ?? []).some((a) => String(a).toUpperCase() === key));
      if (fn) return { name: String(fn.name), description: fn.description ? String(fn.description) : undefined };
      return { name: enclosing };
    }
  }

  // 4) Last resort: top-most function in the formula.
  if (config.functions?.length) {
    const top = getTopMostFunctionName(formula);
    if (top) {
      const key = top.toUpperCase();
      const fn =
        config.functions.find((f) => String(f.name).toUpperCase() === key) ??
        config.functions.find((f) => (f.aliases ?? []).some((a) => String(a).toUpperCase() === key));
      if (fn) return { name: String(fn.name), description: fn.description ? String(fn.description) : undefined };
      return { name: top };
    }
  }

  return {};
}
